/**
 * One branch as a winding path (the Duolingo feel): past steps coloured,
 * the current step large, the next steps faint, steps not yet due softly
 * locked. Tap a step → a sheet in plain words, with the actions this person
 * may take (family: "did it" / "he can do this"; therapist: confirm / step back).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Api } from '@/core/api';
import { fa } from '@/core/jalali';
import { STATUS_FA, type NodeState, type NodeStatus } from '@/core/types';
import { useProfile } from '@/app/ProfileShell';
import { NodeSheet } from './NodeSheet';
import s from './path.module.css';

const FILL: Record<NodeStatus, string> = {
  achieved: 'var(--st-achieved)',
  reported: 'var(--st-reported)',
  emerging: 'var(--st-emerging)',
  not_yet: 'var(--st-notyet-bg)',
  unknown: 'var(--card)',
  not_due: 'var(--st-notdue-bg)',
  lost: 'var(--st-lost)',
};
const INK: Record<NodeStatus, string> = {
  achieved: '#fff',
  reported: '#fff',
  emerging: '#fff',
  not_yet: 'var(--st-notyet)',
  unknown: 'var(--muted)',
  not_due: 'var(--st-notdue)',
  lost: '#fff',
};

export function PathPage() {
  const { id, branch } = useParams({ from: '/authed/p/$id/path/$branch' });
  const profile = useProfile();
  const summary = profile.branches.find((b) => b.branch === branch);
  const q = useQuery({
    queryKey: ['state', id, 'branch', branch],
    queryFn: () => Api.state(id, { branch }),
  });
  const [open, setOpen] = useState<NodeState | null>(null);
  const focusRef = useRef<SVGGElement | null>(null);

  const steps = useMemo(
    () =>
      (q.data?.nodes ?? [])
        .filter((n) => n.kind !== 'profile')
        .sort((a, b) => a.sequence - b.sequence),
    [q.data],
  );
  const profileNodes = useMemo(
    () => (q.data?.nodes ?? []).filter((n) => n.kind === 'profile'),
    [q.data],
  );

  // Geometry: steps go UP the page (the ladder feel), zig-zagging across three columns.
  const W = 320;
  const ROW = 96;
  const cols = [W * 0.25, W * 0.5, W * 0.75];
  const pos = (i: number) => ({ x: cols[[1, 2, 1, 0][i % 4]!]!, y: (steps.length - i) * ROW });
  const H = (steps.length + 1) * ROW;
  const path = steps
    .map((_, i) => {
      const p = pos(i);
      if (i === 0) return `M ${p.x} ${p.y}`;
      const q0 = pos(i - 1);
      const my = (q0.y + p.y) / 2;
      return `C ${q0.x} ${my}, ${p.x} ${my}, ${p.x} ${p.y}`;
    })
    .join(' ');

  // The ladder climbs UP the page, so the start is at the bottom: open the page on the
  // step being worked on, not on the locked steps at the top.
  useEffect(() => {
    focusRef.current?.scrollIntoView({ block: 'center' });
  }, [steps.length]);

  if (!summary)
    return (
      <main className="page">
        <div className="error">این شاخه در دسترس شما نیست.</div>
      </main>
    );

  return (
    <main className="page">
      <div>
        <Link to="/p/$id/map" params={{ id }} className="help">
          ← نقشه
        </Link>
        <h2 className="page-title">{summary.title}</h2>
        <div className="help">
          {fa(summary.counts.achieved)} قدم یاد گرفته از {fa(summary.stepCount)}
          {summary.nextTitle ? ` · قدم بعدی: ${summary.nextTitle}` : ''}
        </div>
      </div>

      {q.isLoading && <div className="skeleton" style={{ minBlockSize: 320 }} />}

      {steps.length > 0 && (
        <div className={s.wrap}>
          <svg viewBox={`0 0 ${W} ${H}`} className={s.svg} role="list" aria-label="قدم‌های مسیر">
            <path d={path} className={s.road} />
            {steps.map((n, i) => {
              const p = pos(i);
              const current = summary.currentNode === n.key;
              const next = summary.nextNode === n.key;
              const r = next ? 30 : current ? 26 : 22;
              return (
                <g
                  key={n.key}
                  ref={next || (!summary.nextNode && current) ? focusRef : undefined}
                  role="listitem"
                  className={s.node}
                  transform={`translate(${p.x} ${p.y})`}
                  onClick={() => setOpen(n)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setOpen(n)}
                >
                  <title>{`${n.label} ${n.title} — ${STATUS_FA[n.status]}`}</title>
                  {next && <circle r={r + 8} className={s.pulse} />}
                  {summary.expectedNode === n.key && (
                    <circle
                      r={r + 5}
                      fill="none"
                      stroke="var(--honey)"
                      strokeWidth={3}
                      strokeDasharray="4 4"
                    />
                  )}
                  <circle
                    r={r}
                    fill={FILL[n.status]}
                    stroke={
                      n.status === 'unknown' || n.status === 'not_due'
                        ? 'var(--line-strong)'
                        : 'transparent'
                    }
                    strokeWidth={2}
                  />
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={INK[n.status]}
                    fontSize={n.status === 'not_due' ? 16 : 13}
                    fontWeight={700}
                  >
                    {n.status === 'achieved'
                      ? '✓'
                      : n.status === 'not_due'
                        ? '🔒'
                        : n.status === 'lost'
                          ? '!'
                          : n.label}
                  </text>
                  <text
                    y={r + 16}
                    textAnchor="middle"
                    fontSize={11.5}
                    fill="var(--ink-2)"
                    className={s.caption}
                  >
                    {n.title.length > 26 ? n.title.slice(0, 25) + '…' : n.title}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      )}

      {profileNodes.length > 0 && (
        <section className="card">
          <div className="overline">کنار مسیر (گزارش می‌شود، رتبه نمی‌دهد)</div>
          {profileNodes.map((n) => (
            <button
              key={n.key}
              type="button"
              className="row spread"
              style={{ minBlockSize: 44, textAlign: 'start' }}
              onClick={() => setOpen(n)}
            >
              <span>{n.title}</span>
              <span className={`chip ${n.status}`}>{STATUS_FA[n.status]}</span>
            </button>
          ))}
        </section>
      )}

      {open && <NodeSheet profileId={id} node={open} onClose={() => setOpen(null)} />}
    </main>
  );
}
